import { buildCandidateKey } from '../features/reviewableItems/candidateKeys';
import {
  buildReviewableOrigin,
  REVIEWABLE_ORIGIN_COLLECTIONS,
  REVIEWABLE_ORIGIN_TYPES,
} from '../features/reviewableItems/reviewableItemFlow';

const normalizeText = (value = '') => String(value || '').trim();

const normalizeConfidence = (value = '') => {
  const confidence = normalizeText(value).toLowerCase();
  if (confidence === 'high' || confidence === 'medium' || confidence === 'low') {
    return confidence;
  }
  return 'low';
};

const DOCUMENT_NEUTRAL_DOMAIN = 'document';

const getFindingLabel = (finding = {}, collection = '') => {
  if (collection === 'dates') {
    return normalizeText(finding.referringTo) || 'Dated fact';
  }
  if (collection === 'sections') {
    return normalizeText(finding.title) || 'Document section';
  }
  if (collection === 'allergyInfo') {
    return normalizeText(finding.description) || 'Allergy finding';
  }
  if (collection === 'goalInfo') {
    return normalizeText(finding.description) || 'Goal finding';
  }
  return normalizeText(finding.label) || normalizeText(finding.title) || 'Document finding';
};

const getFindingSourceText = (finding = {}, collection = '') => {
  if (collection === 'dates') {
    return normalizeText(finding.sourceText || [finding.referringTo, finding.dateText].filter(Boolean).join(': '));
  }
  if (collection === 'sections') {
    return normalizeText(finding.sourceText || finding.body);
  }
  if (collection === 'allergyInfo' || collection === 'goalInfo') {
    return normalizeText(finding.sourceText || finding.description);
  }
  return normalizeText(finding.sourceText || finding.description || finding.body || finding.label || finding.title);
};

const getFindingKind = (collection = '') => {
  if (collection === 'dates') return 'dated_fact';
  if (collection === 'sections') return 'section';
  if (collection === 'allergyInfo') return 'allergy';
  if (collection === 'goalInfo') return 'goal';
  return 'other';
};

const collectionToExtractedType = (collection = '') => {
  if (collection === 'dates') return 'timeline_fact';
  if (collection === 'sections') return 'embedded_section';
  if (collection === 'allergyInfo') return 'allergy_detail';
  if (collection === 'goalInfo') return 'goal_detail';
  return 'document_detail';
};

const stripDecisionFields = (finding = {}) => {
  const {
    promotionDecision,
    needsReview,
    suggestedAction,
    allowedActions,
    ...cleanFinding
  } = finding || {};
  return cleanFinding;
};

const getFindingStableIdentity = (finding = {}) => normalizeText(
  finding.id
  || finding.anchor
  || finding.key
  || finding.uid
  || finding.sourceFindingId
  || finding.stableId
  || finding.stableAnchor
  || ''
);

const buildNeutralDocumentCandidate = ({
  origin,
  finding = {},
  collection = '',
  index = 0,
  documentPurpose = null,
  sourceId = '',
  childId = '',
  documentConfidence = 'low',
} = {}) => {
  const sourceText = getFindingSourceText(finding, collection);
  const label = getFindingLabel(finding, collection);
  const originId = normalizeText(origin?.id || sourceId);
  if (!originId) return null;

  const findingKind = getFindingKind(collection);
  const extractedType = collectionToExtractedType(collection);
  const sourceFinding = stripDecisionFields(finding);
  const stableFindingIdentity = getFindingStableIdentity(finding);
  const sourceAnchor = stableFindingIdentity
    ? [stableFindingIdentity, sourceText || label].filter(Boolean).join('::')
    : [collection, index, sourceText || label].filter(Boolean).join('::');
  const savedOrigin = buildReviewableOrigin({
    id: originId,
    type: origin?.type || REVIEWABLE_ORIGIN_TYPES.CARE_SOURCE,
    collection: origin?.collection || REVIEWABLE_ORIGIN_COLLECTIONS.CARE_SOURCES,
    childId: normalizeText(origin?.childId || childId),
    sourceText,
    metadata: {
      ...(origin?.metadata || {}),
      documentPurpose,
      documentFindingCollection: collection,
      documentFindingIndex: index,
      documentFindingKind: findingKind,
      sourceFinding,
      stableFindingIdentity: stableFindingIdentity || null,
    },
  });

  const provenance = {
    sourceDocumentId: originId,
    childId: normalizeText(origin?.childId || childId),
    sourceText,
    documentFindingCollection: collection,
    documentFindingIndex: index,
    documentFindingKind: findingKind,
    stableFindingIdentity: stableFindingIdentity || null,
  };

  return {
    schemaVersion: 1,
    key: buildCandidateKey({
      originId,
      domain: DOCUMENT_NEUTRAL_DOMAIN,
      label,
      sourceAnchor,
    }),
    origin: savedOrigin,
    domain: DOCUMENT_NEUTRAL_DOMAIN,
    label,
    sourceText,
    confidence: normalizeConfidence(finding?.confidence || documentConfidence),
    documentPurpose,
    extracted: {
      ...sourceFinding,
      findingCollection: collection,
      findingKind,
      extractedType,
    },
    provenance,
    findingMetadata: {
      findingCollection: collection,
      findingKind,
      findingIndex: index,
      extractedType,
      sourceDocumentId: originId,
      childId: normalizeText(origin?.childId || childId),
      sourceText,
      sourceFinding,
      stableFindingIdentity: stableFindingIdentity || null,
      originalFinding: finding,
    },
    payload: {
      findingCollection: collection,
      findingKind,
      findingIndex: index,
      extractedType,
      documentPurpose,
      provenance,
      sourceFinding,
      stableFindingIdentity: stableFindingIdentity || null,
      originalFinding: finding,
    },
  };
};

const flattenDocumentFindingCollections = (findings = {}) => [
  ['dates', Array.isArray(findings.dates) ? findings.dates : []],
  ['sections', Array.isArray(findings.sections) ? findings.sections : []],
  ['allergyInfo', Array.isArray(findings.allergyInfo) ? findings.allergyInfo : []],
  ['goalInfo', Array.isArray(findings.goalInfo) ? findings.goalInfo : []],
  ['other', Array.isArray(findings.other) ? findings.other : []],
];

export const buildNeutralDocumentCandidatesFromFindings = ({
  originId,
  childId = '',
  findings = {},
  documentPurpose = null,
} = {}) => {
  const cleanOriginId = normalizeText(originId);
  if (!cleanOriginId) return [];

  const origin = {
    id: cleanOriginId,
    type: REVIEWABLE_ORIGIN_TYPES.CARE_SOURCE,
    collection: REVIEWABLE_ORIGIN_COLLECTIONS.CARE_SOURCES,
    childId: normalizeText(childId),
    metadata: {
      documentPurpose,
      sourceContext: 'document',
    },
  };

  return flattenDocumentFindingCollections(findings).flatMap(([collection, items]) => (
    items.map((finding, index) => buildNeutralDocumentCandidate({
      origin,
      finding,
      collection,
      index,
      documentPurpose,
      sourceId: cleanOriginId,
      childId,
      documentConfidence: findings?.confidence || 'low',
    })).filter(Boolean)
  ));
};

export default buildNeutralDocumentCandidatesFromFindings;
